import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useDeviceIdInput } from '@controlant/utility-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import { Button } from '@mui/material';

import Loader from '../components/Loader';
import LoggerCheckTableHeaderRow from '../components/LoggerCheckTableHeaderRow';
import LoggerCheckTableRow from '../components/LoggerCheckTableRow';
import { TOAST_TYPE_MAP, ToastCustom } from '../components/ToastCustom';
import { base64Encode, getLocalStoreObject } from '../helpers/DataHelpers';
import { clearAndFocusInputRef, formatNumberThousandsSeparator } from '../helpers/FormattingHelpers';
import { createLoggerId } from '../helpers/LoggerHelpers';
import { getClearScanModalContent } from '../helpers/ModalContentHelpers';
import { updateListElementWithNewCheckData } from '../helpers/ScanningHelpers';
import GenericModal from '../modals/GenericModal';
import { useRunLoggerCheckMutation } from '../services/hooks/useRunLoggerCheck';
import type { LoggerListItem } from '../types/Logger';
import { GenericModalData } from '../types/Modal';

import '../styles/views/QualityAssuranceCheck.less';
import '../styles/components/LoggerCheckTable.less';

const QualityAssuranceCheck = (): ReactElement => {
    const loggerIdInputRef = useRef<HTMLInputElement>(null);
    const initialMount = useRef<boolean>(true);
    const mode = 'QA-CHECK';

    const [loggerList, setLoggerList] = useState<Array<LoggerListItem>>([]);
    const [loadingLocalStore, setLoadingLocalStore] = useState<boolean>(false);
    const [modalData, setModalData] = useState<GenericModalData | null>(null);

    const { mutate: runLoggerCheck } = useRunLoggerCheckMutation({
        onSuccess: (res) => {
            const loggers = Object.values(res);
            setLoggerList((prevState) => updateListElementWithNewCheckData(loggers, prevState));
        },
        onError: (err, variables) => {
            handleRemoveLoggers([variables.payload.loggerIds[0]]);
            ToastCustom(err?.response?.data.message ?? 'Something went wrong. Please try again.', TOAST_TYPE_MAP.ERROR);
        }
    });

    const { handleInputChange } = useDeviceIdInput({
        onValidId: async (formattedId) => {
            const validatedId = createLoggerId(formattedId);

            const loggerAlreadyAdded = loggerList.some((elem) => elem.loggerId === validatedId);
            if (loggerAlreadyAdded) {
                ToastCustom(`Logger ${validatedId} is already in the scan`, TOAST_TYPE_MAP.WARNING, '', validatedId);
                return;
            }

            setLoggerList((prevState) => [
                { loggerId: validatedId, masterStatus: 'pending', customer: {} },
                ...prevState
            ]);

            runLoggerCheck({
                boxId: undefined,
                scanId: undefined,
                queryParams: {
                    scan_source: mode.toLocaleLowerCase()
                },
                payload: {
                    loggerIds: [validatedId],
                    allowUnapprovedCalibrations: false,
                    useCachedResults: false
                }
            });
        },
        onInvalidId: (reason) => ToastCustom(reason, TOAST_TYPE_MAP.ERROR),
        onSettled: () => clearAndFocusInputRef(loggerIdInputRef)
    });

    useEffect(() => {
        setLoadingLocalStore(true);
        loggerIdInputRef?.current?.focus();
        const localStore = getLocalStoreObject(mode);
        if (localStore) {
            setStateFromStorage(getLocalStoreObject(mode));
        } else {
            clearScan();
        }
        setLoadingLocalStore(false);
    }, [mode]);

    useEffect(() => {
        if (initialMount.current) {
            initialMount.current = false;
            return;
        }
        updateStorage();
    }, [loggerList]);

    const updateStorage = () => {
        const storageObject = {
            loggerList: loggerList.filter((elem: any) => elem.masterStatus !== 'pending')
        };
        localStorage.setItem(mode, base64Encode(JSON.stringify(storageObject)));
    };

    const setStateFromStorage = (storage: { [key: string]: any }) => {
        const storeLoggerList = storage['loggerList'];
        if (storeLoggerList?.length) {
            const list = storeLoggerList.filter((elem: LoggerListItem) => elem.masterStatus !== 'pending');
            setLoggerList(list.length ? list : []);
        }
    };

    const handleRemoveLoggers = (loggersToRemove: Array<string>) => {
        const loggerIdsToRemove = new Map(loggersToRemove.map((loggerId) => [loggerId, true]));

        setLoggerList((prevState) => prevState.filter(({ loggerId }) => !loggerIdsToRemove.has(loggerId)));
    };

    const clearScan = () => {
        setModalData(null);
        setLoggerList([]);
        localStorage.removeItem(mode);
    };

    return (
        <div className="logger-checks-wrapper">
            <h1>Quality Assurance Check</h1>

            <Loader size={20} loading={loadingLocalStore} />

            <div className="section bottom-section">
                <label className="control-label">Logger ID</label>

                <div className="cal-input-button-container">
                    <div className="input-error-container">
                        <input
                            className="large"
                            onChange={(e) => handleInputChange(e.target.value)}
                            placeholder="Scan or enter Logger ID"
                            ref={loggerIdInputRef}
                        />
                    </div>

                    <Button
                        variant="contained"
                        color="warning"
                        disabled={!loggerList.length}
                        onClick={() => {
                            setModalData({
                                ...getClearScanModalContent(),
                                confirmAction: () => clearScan(),
                                closeMe: () => setModalData(null)
                            });
                            loggerIdInputRef?.current?.focus();
                        }}
                    >
                        Clear Scan
                    </Button>
                </div>
            </div>

            <div className="logger-table-wrapper">
                <div className="table-container logger-table">
                    <LoggerCheckTableHeaderRow scanType={mode} />

                    <div>
                        {loggerList?.length > 0 &&
                            !loadingLocalStore &&
                            loggerList.map((logger: LoggerListItem, index) => (
                                <LoggerCheckTableRow
                                    key={logger.loggerId}
                                    logger={logger}
                                    rowNumber={index + 1}
                                    scanType={mode}
                                    onDeleteClick={() => handleRemoveLoggers([logger.loggerId])}
                                    isFlagAsDamagedEnabled={false}
                                    isDeleting={false}
                                    isLoading={false}
                                />
                            ))}
                        {loggerList && !loggerList.length && (
                            <div className="row empty-row" data-testid="empty-row">
                                <DocumentScannerIcon fontSize="small" className="margin-right-extra" /> Please scan
                                loggers
                            </div>
                        )}
                        {loadingLocalStore && (
                            <div className="empty-row" data-testid="loading-row">
                                <div className="info-text">
                                    <FontAwesomeIcon icon="spinner" spin={true} /> Loading...
                                </div>
                            </div>
                        )}
                        <div className="footer-row" data-testid="footer-row">
                            <strong>{formatNumberThousandsSeparator(loggerList.length)} items</strong>
                        </div>
                    </div>
                </div>
            </div>

            <GenericModal modalData={modalData} />
        </div>
    );
};

export default QualityAssuranceCheck;
